import { detectOS } from '../../hooks/useHotkeys';
import type { ShortcutBinding } from './types';

type DisplayOS = 'mac' | 'windows';

const MAC_SYMBOLS: Record<string, string> = {
  primary: '⌘',
  ctrl: '⌃',
  alt: '⌥',
  shift: '⇧',
  meta: '⌘',
};

const KEY_LABELS: Record<string, string> = {
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  escape: 'Esc',
  delete: 'Del',
  enter: '↵',
  ' ': 'Space',
};

function formatKeyToken(segment: string, os: DisplayOS): string {
  if (os === 'mac' && MAC_SYMBOLS[segment]) {
    return MAC_SYMBOLS[segment];
  }
  if (segment === 'primary') {
    return os === 'mac' ? '⌘' : 'Ctrl';
  }
  if (segment === 'ctrl') return os === 'mac' ? '⌃' : 'Ctrl';
  if (segment === 'alt') return os === 'mac' ? '⌥' : 'Alt';
  if (segment === 'shift') return os === 'mac' ? '⇧' : 'Shift';
  if (KEY_LABELS[segment]) return KEY_LABELS[segment];
  if (segment.length === 1) return segment.toUpperCase();
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

function tokenizeComboPart(part: string, os: DisplayOS): string[] {
  const segments = part
    .split('+')
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean);

  return segments.map((segment) => formatKeyToken(segment, os));
}

/**
 * Token groups for visual key-cap rendering: outer = chord steps, inner = individual keys.
 */
export function getKeyboardShortcutKeyTokens(
  binding: ShortcutBinding,
  os: DisplayOS = detectOS() === 'mac' ? 'mac' : 'windows',
): string[][] {
  const parts = Array.isArray(binding) ? binding : [binding];
  return parts.map((part) => tokenizeComboPart(part, os));
}

function formatComboPart(part: string, os: DisplayOS): string {
  return tokenizeComboPart(part, os).join(os === 'mac' ? '' : '+');
}

/**
 * OS-aware display for a shortcut binding (combo or chord).
 */
export function formatKeyboardShortcut(
  binding: ShortcutBinding,
  os: DisplayOS = detectOS() === 'mac' ? 'mac' : 'windows',
): string {
  if (Array.isArray(binding)) {
    // Chord: press first combo, release, then second key within ~1.5s (GDrive-style).
    return binding.map((part) => formatComboPart(part, os)).join(' + ');
  }

  return formatComboPart(binding, os);
}
